import {useCallback, useEffect, useRef, useState} from "react"

const identity = (item) => item

const usePaginatedExecutions = ({fetchPage, normalizeItem = identity, active = true}) => {
    const [rows, setRows] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const requestSequence = useRef(0)

    const load = useCallback(async ({silent = false} = {}) => {
        const requestId = ++requestSequence.current
        if (!silent) setLoading(true)
        try {
            const result = await fetchPage({page, pageSize})
            if (requestId !== requestSequence.current) return

            const items = Array.isArray(result?.items) ? result.items : []
            const nextTotal = Math.max(0, Number(result?.total ?? items.length) || 0)
            const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize))
            if (page > lastPage) {
                setPage(lastPage)
                return
            }
            setRows(items.map(normalizeItem))
            setTotal(nextTotal)
            setError(null)
        } catch (loadError) {
            if (requestId !== requestSequence.current) return
            setError(loadError)
            if (!silent) setRows([])
        } finally {
            if (requestId === requestSequence.current && !silent) setLoading(false)
        }
    }, [fetchPage, normalizeItem, page, pageSize])

    useEffect(() => {
        if (active) load()
        return () => {
            requestSequence.current += 1
        }
    }, [active, load])

    const changePage = useCallback((nextPage, nextPageSize = pageSize) => {
        const normalizedSize = Math.max(1, Number(nextPageSize) || 25)
        setPageSize(normalizedSize)
        setPage(Math.max(1, Number(nextPage) || 1))
    }, [pageSize])

    const resetPage = useCallback(() => setPage(1), [])

    return {
        rows,
        total,
        page,
        pageSize,
        loading,
        error,
        load,
        changePage,
        resetPage,
    }
}

export default usePaginatedExecutions
